/**
 * The manager pages' shared frame (manager redesign, 2026-10-09): one page header with its actions,
 * local tabs, quiet notices, a closed explanation, and a detail panel that sits beside the list and
 * becomes a sheet on a phone. Styles in page.css, scoped to these classes so Vaktplan is untouched.
 */
import { T } from '@/i18n';
import { Icon } from '@/ui/icons';
import { type KeyboardEvent, type ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import './page.css';

export function PageHead({
  title,
  sub,
  actions,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  /** the local tabs, under the title */
  children?: ReactNode;
}) {
  return (
    <header className="mh">
      <div className="mhrow">
        <div className="mht">
          <h1 tabIndex={-1}>{title}</h1>
          {sub != null && <p className="mhs">{sub}</p>}
        </div>
        {actions != null && <div className="mha">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

export interface TabItem {
  key: string;
  label: ReactNode;
  /** a tab that is an address: the browser's Back and a bookmark land on it */
  to?: string;
  count?: number | null;
  /** an accessible name when the label alone is ambiguous, with the count in words */
  aria?: string;
}

/** The ids that tie a tab to its panel; TabPanel below derives the same pair (issue #82). */
const tabId = (key: string) => `tab-${key}`;
const panelId = (key: string) => `tabpanel-${key}`;

/**
 * The address of a tab just clicked to another page (issue #79). That click unmounts its tablist,
 * so the browser drops focus to the body; the tablist that mounts selected on this address takes
 * focus once. A plain link to the same page leaves it null, so nothing moves.
 */
let pending: string | null = null;

/**
 * Manager tabs: one tab stop, arrow keys, Home and End, focus kept on the chosen tab after it
 * renders (consistency fixes, 2026-10-09). A tab with `to` is a link, so the address names it.
 */
export function Tabs({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: TabItem[];
  value: string;
  onChange?: (key: string) => void;
  label: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // a same-page tab keeps its element, so this finds it focused already and only clears the flag
  useEffect(() => {
    if (pending == null) return;
    const on = ref.current?.querySelector<HTMLElement>(`[id="${tabId(value)}"]`);
    if (on?.getAttribute('href') === pending) on.focus();
    pending = null;
  }, [value]);
  const move = (e: KeyboardEvent) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const i = items.findIndex((x) => x.key === value);
    const n = items.length;
    const j =
      e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
    const el = ref.current?.querySelectorAll<HTMLElement>('[role=tab]')[j];
    el?.click();
    el?.focus();
  };
  return (
    <div
      className={`mtabs${className ? ` ${className}` : ''}`}
      role="tablist"
      aria-label={label}
      ref={ref}
      onKeyDown={move}
    >
      {items.map((x) => {
        const on = x.key === value;
        return x.to ? (
          <NavLink
            key={x.key}
            id={tabId(x.key)}
            to={x.to}
            role="tab"
            aria-selected={on}
            aria-controls={panelId(x.key)}
            aria-label={x.aria}
            tabIndex={on ? 0 : -1}
            className={on ? 'on' : ''}
            onClick={() => {
              if (!on) pending = x.to ?? null;
            }}
            end
          >
            <TabBody label={x.label} count={x.count ?? null} />
          </NavLink>
        ) : (
          <button
            key={x.key}
            id={tabId(x.key)}
            type="button"
            role="tab"
            aria-selected={on}
            aria-controls={panelId(x.key)}
            aria-label={x.aria}
            tabIndex={on ? 0 : -1}
            className={on ? 'on' : ''}
            onClick={() => onChange?.(x.key)}
          >
            <TabBody label={x.label} count={x.count ?? null} />
          </button>
        );
      })}
    </div>
  );
}

/** The selected tab's content, named by its tab; one per page, under the PageHead (issue #82). */
export const TabPanel = ({ tab, children }: { tab: string; children: ReactNode }) => (
  <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
    {children}
  </div>
);

const TabBody = ({ label, count }: { label: ReactNode; count: number | null }) => (
  <>
    {label}
    {count != null && count > 0 && <span className="mtc">{count}</span>}
  </>
);

/** A short closed explanation beside the thing it explains ("Hva teller som et punkt?"). */
export function Explain({ q, children, className }: { q: string; children: ReactNode; className?: string }) {
  return (
    <details className={`mexp${className ? ` ${className}` : ''}`}>
      <summary>{q}</summary>
      <div className="mexb">{children}</div>
    </details>
  );
}

export function Notice({
  tone = 'warn',
  title,
  children,
  action,
}: {
  tone?: 'warn' | 'dgr' | 'info' | 'ok';
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`mnote ${tone}`} role={tone === 'dgr' ? 'alert' : undefined}>
      <Icon name={tone === 'dgr' ? 'stop' : tone === 'warn' ? 'warn' : tone === 'ok' ? 'check' : 'info'} />
      <div className="mnt">
        <b>{title}</b>
        {children != null && <div>{children}</div>}
      </div>
      {action != null && <div className="mna">{action}</div>}
    </div>
  );
}

/**
 * The selected object's details beside the list (desktop) or as a sheet over it (phone). Escape and
 * the close button both go through onClose, which may refuse (an unsaved note asks first). Focus
 * moves into the panel on open and back to what opened it on close.
 */
export function DetailPanel({
  title,
  sub,
  onClose,
  children,
  footer,
  label,
  restoreFocusTo,
}: {
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  label?: string;
  /** Row action fallback for a panel opened from a direct URL instead of an in-page trigger. */
  restoreFocusTo?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const opener = useRef<Element | null>(null);
  const returnTarget = useRef(restoreFocusTo);
  returnTarget.current = restoreFocusTo;
  useLayoutEffect(() => {
    opener.current = document.activeElement;
    ref.current?.querySelector<HTMLElement>('h2')?.focus();
    return () => {
      const back = opener.current as HTMLElement | null;
      if (back?.isConnected && back !== document.body && back !== document.documentElement) back.focus();
      else {
        const row = returnTarget.current ? document.querySelector<HTMLElement>(returnTarget.current) : null;
        (row ?? document.querySelector<HTMLElement>('#main h1'))?.focus();
      }
    };
  }, []);
  return (
    <aside
      className="mpanel"
      ref={ref}
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="mph">
        <div>
          <h2 tabIndex={-1}>{title}</h2>
          {sub != null && <p>{sub}</p>}
        </div>
        <button type="button" className="mpx" aria-label={T('Lukk')} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <div className="mpb">{children}</div>
      {footer != null && <div className="mpf">{footer}</div>}
    </aside>
  );
}

/** A label and value row in a detail panel. */
export const Fact = ({ k, children }: { k: ReactNode; children: ReactNode }) => (
  <div className="mfact">
    <span>{k}</span>
    <b>{children}</b>
  </div>
);

/** Initials for a person's avatar. */
export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/** A status label in a row: a quiet filled tag, the words carry the meaning, the colour supports it. */
export const Status = ({
  tone,
  children,
}: {
  tone: 'ok' | 'warn' | 'dgr' | 'neu' | 'act';
  children: ReactNode;
}) => (
  <span className={`mstat ${tone}`}>
    {(tone === 'warn' || tone === 'dgr') && <Icon name={tone === 'dgr' ? 'stop' : 'warn'} />}
    <span>{children}</span>
  </span>
);
